import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AFL_SEASON_SHORT_LABEL,
    BookmakerLogo,
    BOOKMAKER_DISPLAY_NAMES,
    formatOdds,
    formatPct,
    formatStartTime,
    shortenMatchName,
    computeMultiMetrics,
    computeBookmakerComparison,
} from './multiBuilderHelpers';

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center text-center px-6 py-10 flex-1">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-400/70" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
                </svg>
            </div>
            <h4 className="text-sm font-semibold font-mono text-slate-200 tracking-tight">No legs added</h4>
            <p className="text-[11px] font-mono text-slate-500 mt-2 leading-relaxed max-w-[220px]">
                Tap a checkbox on a value bet to add it to your multi. Combined odds, edge and bookmaker comparisons populate here.
            </p>
        </div>
    );
}

function LegRow({ leg, onRemove, anchorMissing }) {
    return (
        <div className={`relative pl-3 pr-2 py-2.5 border-b border-slate-700/30 transition-colors ${
            anchorMissing ? 'bg-red-500/5' : 'hover:bg-slate-800/30'
        }`}>
            <span className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-r ${anchorMissing ? 'bg-red-400/60' : 'bg-emerald-400/40'}`} />
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                        <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                            <span className="text-xs font-semibold text-slate-200 whitespace-nowrap flex-shrink-0" title={leg.player}>
                                {leg.player}
                            </span>
                            <span className="text-slate-700 flex-shrink-0">·</span>
                            <span className="text-[11px] font-mono text-slate-400 truncate min-w-0" title={leg.market}>
                                {leg.market}
                            </span>
                        </div>
                        <span className={`text-[13px] font-mono font-bold tabular-nums whitespace-nowrap ${
                            anchorMissing ? 'text-red-400' : 'text-emerald-400'
                        }`}>
                            {anchorMissing ? '—' : formatOdds(leg.offeredOdds)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider truncate min-w-0">
                            {shortenMatchName(leg.match, 'afl')}
                            {leg.startTime && (
                                <>
                                    <span className="text-slate-700"> · </span>
                                    <span>{formatStartTime(leg.startTime)}</span>
                                </>
                            )}
                        </span>
                        {anchorMissing && (
                            <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded border border-red-500/30 bg-red-500/5 text-red-400 whitespace-nowrap flex-shrink-0">
                                Off market
                            </span>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onRemove(leg)}
                    aria-label={`Remove ${leg.player} leg`}
                    className="flex-shrink-0 p-1 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            {!anchorMissing && (
                <div className="mt-1 flex items-center justify-end gap-1.5 text-[10px] font-mono text-slate-500 tabular-nums whitespace-nowrap">
                    <span>
                        avg {formatOdds(leg.avgMarketOdds)}
                        {Number.isFinite(leg.impliedEdgePercentage) && (
                            <span className={`ml-1 ${leg.impliedEdgePercentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ({leg.impliedEdgePercentage >= 0 ? '+' : ''}{leg.impliedEdgePercentage.toFixed(1)}%)
                            </span>
                        )}
                    </span>
                    {Number.isFinite(leg.impliedOdds) && (
                        <>
                            <span className="text-slate-700">·</span>
                            <span>
                                implied {formatOdds(leg.impliedOdds)}
                                {Number.isFinite(leg.impliedEv) && (
                                    <span className={`ml-1 ${leg.impliedEv >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        ({leg.impliedEv >= 0 ? '+' : ''}{Number(leg.impliedEv).toFixed(1)}%)
                                    </span>
                                )}
                            </span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Generic info-circle icon with a hover/focus tooltip. The tooltip is portaled
 * to `document.body` and positioned with `fixed` coordinates so it escapes the
 * panel's `overflow-hidden` clipping.
 *
 * `accent` controls the colour palette:
 *   - "amber"   — warning context (e.g. non-correlated odds).
 *   - "emerald" — neutral/info context (default; matches the desktop info icons).
 */
function InfoIcon({ accent = 'emerald', ariaLabel, title, children }) {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState(null);

    const showTooltip = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const placeBelow = rect.top < 140;
        setCoords({
            top: placeBelow ? rect.bottom + 6 : undefined,
            bottom: placeBelow ? undefined : window.innerHeight - rect.top + 6,
            right: Math.max(8, window.innerWidth - rect.right),
        });
    }, []);
    const hideTooltip = useCallback(() => setCoords(null), []);

    const triggerTone = accent === 'amber'
        ? 'text-amber-400/80 hover:text-amber-300 focus:text-amber-300'
        : 'text-slate-500 hover:text-emerald-300 focus:text-emerald-300';
    const tooltipBorder = accent === 'amber' ? 'border-amber-500/30' : 'border-slate-700/60';
    const tooltipTitleTone = accent === 'amber' ? 'text-amber-300' : 'text-emerald-300';

    return (
        <>
            <span
                ref={triggerRef}
                tabIndex={0}
                role="button"
                aria-label={ariaLabel || title}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideTooltip}
                className={`inline-flex items-center cursor-help focus:outline-none transition-colors ${triggerTone}`}
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" strokeWidth="1.8" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 16v-4M12 8h.01" />
                </svg>
            </span>
            {coords && createPortal(
                <div
                    role="tooltip"
                    className={`pointer-events-none fixed z-[10001] w-60 max-w-[calc(100vw-1rem)]
                        px-3 py-2.5 rounded-lg
                        bg-slate-900/95 backdrop-blur border ${tooltipBorder} shadow-xl shadow-black/50
                        whitespace-normal break-words animate-fade-in`}
                    style={{ top: coords.top, bottom: coords.bottom, right: coords.right }}
                >
                    <span className={`block text-[10px] font-mono font-semibold uppercase tracking-widest mb-1 ${tooltipTitleTone}`}>
                        {title}
                    </span>
                    <span className="block text-[11px] font-mono text-slate-300 leading-relaxed">
                        {children}
                    </span>
                </div>,
                document.body
            )}
        </>
    );
}

function MetricCell({ label, value, sub, accent, info }) {
    const color = accent === 'red'
        ? 'text-red-400'
        : accent === 'emerald'
        ? 'text-emerald-400'
        : 'text-white';
    return (
        <div className="relative min-w-0 px-2.5 py-2 bg-slate-900/60 border border-slate-700/30 rounded">
            {info && (
                <span className="absolute top-1.5 right-1.5">
                    <InfoIcon title={info.title} ariaLabel={`${label} info`}>
                        {info.body}
                    </InfoIcon>
                </span>
            )}
            <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest leading-none pr-4">
                {label}
            </div>
            <div className={`text-base font-mono font-bold tabular-nums mt-1 ${color}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[9px] font-mono text-slate-600 tabular-nums mt-0.5 truncate">{sub}</div>
            )}
        </div>
    );
}

function MultiBuilderPanel({
    legs,
    multiBookmaker,
    aflSeasonView,
    bookmakerOddsByLeg,
    onRemoveLeg,
    onClearAll,
    onAddToTracker,
    onSubmitTip,
    chromeless = false,
}) {
    const metrics = useMemo(() => computeMultiMetrics(legs), [legs]);
    const comparison = useMemo(
        () => computeBookmakerComparison(legs, bookmakerOddsByLeg),
        [legs, bookmakerOddsByLeg]
    );

    const anchorRow = comparison.find((r) => r.bookmaker === multiBookmaker);
    const otherRows = comparison.filter((r) => r.bookmaker !== multiBookmaker);
    const bestBookmakerRow = comparison.length > 0 ? comparison[0] : null;

    const hasLegs = legs.length > 0;
    const seasonShort = AFL_SEASON_SHORT_LABEL[aflSeasonView] || aflSeasonView || '';

    // True when two (or more) selected legs share a game. Bookmakers apply their
    // own SGM correlation models in this case, so the combined odds displayed in
    // both the ticket banner and the Compare books table are independent-event
    // products that won't match a bookmaker's actual SGM payout.
    const hasSameGameLegs = useMemo(() => {
        if (legs.length < 2) return false;
        const seen = new Set();
        for (const leg of legs) {
            const key = leg.match || '';
            if (!key) continue;
            if (seen.has(key)) return true;
            seen.add(key);
        }
        return false;
    }, [legs]);

    // Identify legs that are no longer available at the multi's bookmaker.
    const missingByLeg = useMemo(() => {
        const m = new Set();
        if (!multiBookmaker) return m;
        legs.forEach((leg) => {
            const oddsForLeg = bookmakerOddsByLeg[leg.key] || {};
            if (oddsForLeg[multiBookmaker] == null) m.add(leg.key);
        });
        return m;
    }, [legs, bookmakerOddsByLeg, multiBookmaker]);

    return (
        <aside className={`flex flex-col h-full overflow-hidden ${
            chromeless ? '' : 'bg-slate-900/60 border border-slate-700/40 rounded-lg'
        }`}>
            {/* Header — ticket-style banner. When `chromeless` (mobile sheet) we drop
                the gradient + perforations because the panel isn't rendered as a
                stand-alone card; the gradient otherwise reads as a separate dark
                rectangle inside the sheet's flat slate-900 background. */}
            <div className={`relative px-4 pt-4 pb-3 border-b border-dashed border-slate-700/60 ${
                chromeless ? '' : 'bg-gradient-to-br from-slate-900 to-slate-950'
            }`}>
                {/* Decorative perforations — only meaningful when the panel renders as
                    its own ticket-shaped card. Hidden in the sheet context. */}
                {!chromeless && (
                    <>
                        <span aria-hidden="true" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />
                        <span aria-hidden="true" className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />
                    </>
                )}

                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
                            </svg>
                            <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-[0.18em] leading-none">
                                Multi Builder
                            </span>
                        </div>
                        <div className="flex items-baseline gap-2 mt-2">
                            <span className="text-2xl font-mono font-bold tabular-nums text-white leading-none">
                                {hasLegs ? metrics.combinedOdds?.toFixed(2) ?? '—' : '—'}
                            </span>
                            <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                                {hasSameGameLegs ? 'non-correlated odds' : 'combined'}
                            </span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Legs</div>
                        <div className="text-xl font-mono font-bold tabular-nums text-white leading-none mt-1">
                            {legs.length}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 min-w-0">
                        {multiBookmaker ? (
                            <>
                                <BookmakerLogo bookmaker={multiBookmaker} size={16} />
                                <span className="text-[11px] font-mono text-slate-300 truncate">
                                    {BOOKMAKER_DISPLAY_NAMES[multiBookmaker] || multiBookmaker}
                                </span>
                            </>
                        ) : (
                            <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                                Pick a leg to start your multi
                            </span>
                        )}
                    </div>
                    {hasLegs && (
                        <button
                            type="button"
                            onClick={onClearAll}
                            className="text-[10px] font-mono text-slate-500 hover:text-red-400 transition-colors uppercase tracking-widest"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Body */}
            {!hasLegs ? (
                <EmptyState />
            ) : (
                <>
                    {/* Legs list */}
                    <div className="flex-1 min-h-0 overflow-y-auto styled-scrollbar">
                        {legs.map((leg) => (
                            <LegRow
                                key={leg.key}
                                leg={leg}
                                onRemove={onRemoveLeg}
                                anchorMissing={missingByLeg.has(leg.key)}
                            />
                        ))}
                    </div>

                    {/* Metrics */}
                    <div className="px-3 py-2.5 border-t border-slate-700/40">
                        <div className="grid grid-cols-2 gap-2">
                            <MetricCell
                                label="Market EV%"
                                value={metrics.marketEvPct != null ? formatPct(metrics.marketEvPct, { digits: 2 }) : '—'}
                                accent={metrics.marketEvPct != null && metrics.marketEvPct >= 0 ? 'emerald' : metrics.marketEvPct != null ? 'red' : undefined}
                                sub={metrics.fairCombinedOdds != null ? ` avg market price ${formatOdds(metrics.fairCombinedOdds)}` : null}
                                info={{
                                    title: 'Market EV%',
                                    body: (
                                        <>
                                            Expected value of the multi vs the bookmaker consensus.
                                            Compares the combined offered odds to the combined
                                            <span className="text-slate-200"> average market price </span>
                                            across all books. Positive means the multi pays more
                                            than the consensus price implies.
                                        </>
                                    ),
                                }}
                            />
                            <MetricCell
                                label={`Implied EV% (${seasonShort})`}
                                value={metrics.historicalEvPct != null ? formatPct(metrics.historicalEvPct, { digits: 2 }) : '—'}
                                accent={metrics.historicalEvPct != null && metrics.historicalEvPct >= 0 ? 'emerald' : metrics.historicalEvPct != null ? 'red' : undefined}
                                sub={metrics.combinedImpliedOdds != null ? `implied ${formatOdds(metrics.combinedImpliedOdds)}` : 'no hit-rate data'}
                                info={{
                                    title: `Implied EV% (${seasonShort})`,
                                    body: (
                                        <>
                                            Expected value of the multi vs historical hit rates.
                                            Multiplies the combined offered odds by each leg&apos;s
                                            historical hit rate from the
                                            <span className="text-slate-200"> {seasonShort} </span>
                                            dataset. Positive means the multi pays more than the
                                            historical hit rates imply.
                                        </>
                                    ),
                                }}
                            />
                        </div>
                    </div>

                    {/* Bookmaker comparison */}
                    <div className="px-3 py-2.5 border-t border-slate-700/40 bg-slate-950/40">
                        <div className="flex items-center justify-between mb-2 gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                                    Compare books
                                </span>
                                {hasSameGameLegs && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-[9px] font-mono font-semibold text-amber-300 uppercase tracking-widest">
                                        Non-correlated odds
                                    </span>
                                )}
                            </div>
                            {bestBookmakerRow && multiBookmaker && bestBookmakerRow.bookmaker !== multiBookmaker && !hasSameGameLegs && (
                                <span className="text-[10px] font-mono text-amber-300/80 uppercase tracking-widest">
                                    Best: {BOOKMAKER_DISPLAY_NAMES[bestBookmakerRow.bookmaker] || bestBookmakerRow.bookmaker}
                                </span>
                            )}
                        </div>

                        {hasSameGameLegs && (
                            <div
                                role="note"
                                className="mb-2 flex items-start gap-2 px-2.5 py-2 rounded-md border border-amber-500/25 bg-amber-500/5"
                            >
                                <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
                                </svg>
                                <p className="text-[10.5px] font-mono text-amber-200/90 leading-snug">
                                    Your multi contains legs from the same game. Bookmakers price
                                    same-game multis using their own correlation models, so these
                                    combined odds assume independent events and won&apos;t reflect
                                    each book&apos;s actual SGM payout.
                                </p>
                            </div>
                        )}

                        {comparison.length === 0 ? (
                            <div className="text-[11px] font-mono text-slate-600 leading-relaxed py-1.5">
                                No bookmaker covers every leg.
                            </div>
                        ) : (
                            <div className="rounded-md border border-slate-700/40 divide-y divide-slate-800/60 overflow-hidden">
                                {anchorRow && (
                                    <BookmakerCompareRow
                                        row={anchorRow}
                                        isAnchor
                                        nonCorrelated={hasSameGameLegs}
                                    />
                                )}
                                {otherRows.map((row) => (
                                    <BookmakerCompareRow
                                        key={row.bookmaker}
                                        row={row}
                                        nonCorrelated={hasSameGameLegs}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Sticky CTA row — Tip + Add to Tracker. Tip is rendered as an
                        amber-bordered ghost button to mirror SnipeFeed's tip palette,
                        and is only included when the caller passes `onSubmitTip`
                        (i.e. the user has the COMMUNITY_TIPPER role). Both CTAs share
                        the same disabled state — a multi needs ≥ 2 legs to mean
                        anything; a single-leg "multi" is just a straight bet. */}
                    {(onAddToTracker || onSubmitTip) && (
                        <div className="px-3 py-2.5 border-t border-slate-700/40 flex-shrink-0 bg-slate-950/60">
                            <div className="flex items-stretch gap-2">
                                {onSubmitTip && (
                                    <button
                                        type="button"
                                        onClick={onSubmitTip}
                                        disabled={legs.length < 2}
                                        aria-label="Submit community tip"
                                        title="Submit community tip"
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-xs font-mono font-semibold tracking-wide text-amber-300 border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-400/60 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-amber-500/5 disabled:hover:border-amber-500/40"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Tip
                                    </button>
                                )}
                                {onAddToTracker && (
                                    <button
                                        type="button"
                                        onClick={onAddToTracker}
                                        disabled={legs.length < 2}
                                        className={`${onSubmitTip ? 'flex-[2]' : 'flex-1'} inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-xs font-mono font-semibold tracking-wide text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:from-emerald-600 disabled:hover:to-teal-600`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add to Tracker
                                    </button>
                                )}
                            </div>
                            {legs.length < 2 && (
                                <p className="mt-1.5 text-[9.5px] font-mono text-slate-600 uppercase tracking-widest text-center">
                                    Add at least 2 legs to {onSubmitTip ? 'tip or save' : 'save'} a multi
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
        </aside>
    );
}

function NonCorrelatedInfoIcon() {
    return (
        <InfoIcon accent="amber" title="Non-correlated odds" ariaLabel="Non-correlated odds info">
            These combined odds assume independent events. Bookmakers apply their own
            correlation models for same-game multis, so the actual SGM payout will differ.
        </InfoIcon>
    );
}

function BookmakerCompareRow({ row, isAnchor = false, nonCorrelated = false }) {
    // Highlight ("best") styling is suppressed when odds are non-correlated since
    // the "best" comparison is itself misleading under SGM correlation pricing.
    const showAnchor = isAnchor && !nonCorrelated;
    const oddsTone = nonCorrelated
        ? 'text-amber-200/90'
        : showAnchor
            ? 'text-emerald-300'
            : 'text-white';
    const labelTone = nonCorrelated
        ? 'text-slate-300'
        : showAnchor
            ? 'text-emerald-200 font-semibold'
            : 'text-slate-300';
    const rowBg = showAnchor ? 'bg-emerald-500/[0.06]' : 'bg-slate-900/30';
    return (
        <div className={`flex items-center justify-between gap-2 px-2.5 py-1.5 ${rowBg}`}>
            <div className="flex items-center gap-2 min-w-0">
                <BookmakerLogo bookmaker={row.bookmaker} size={14} />
                <span className={`text-[11px] font-mono truncate ${labelTone}`}>
                    {BOOKMAKER_DISPLAY_NAMES[row.bookmaker] || row.bookmaker}
                </span>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-xs font-mono font-bold tabular-nums ${oddsTone}`}>
                {nonCorrelated && <NonCorrelatedInfoIcon />}
                {formatOdds(row.combinedOdds)}
            </span>
        </div>
    );
}

export default MultiBuilderPanel;
